import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import SlideRightDialog from '../../../components/common/SlideRightDialog';
import SlideDialogV from '../../../components/common/SlideDialogV';
import InfoBanner from '../../../components/Banner';
import FileUploadSection from './FileUploadSection';
import { uploadPDF, setReload, getPDFUploadCheck, pdfConfirm } from '../eDocumentsSlice';
import { getEDocumentErrorMessages } from '../util';
import { getAlertMessageFunction, magamCertifyCheck } from '../../../common/Util';
import { TARGET_YEAR } from '../../../common/data';

const UploadPdf = ({ isOpen, setIsOpen }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('eDocuments');
  const fileInputRef = useRef(null);
  const detailFileInputRef = useRef(null);
  const { uploadFlag, eDocumentStatus, OriginFileList } = useSelector(state => state.eDocuments);
  const { isMagam, isCertify } = useSelector(state => state.common);
  const { myInfo } = useSelector(state => state.myInfo);
  const [fileList, setFileList] = useState([]);

  //상세일 때 파일 업로드 안된 경우 표시
  const [notAllFile, setNotAllFile] = useState(false);

  // 업로드 진행률
  const [uploadProgress, setUploadProgress] = useState({});

  // 에러 슬라이드 다이얼로그 상태
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogDescription, setErrorDialogDescription] = useState('');

  useEffect(() => {
    console.log('OriginFileList', OriginFileList);
    if (OriginFileList.length === 0) {
      setFileList([]);
      return;
    }

    const newFileList = OriginFileList.map(item => ({
      fileId: `${item.htmDocType}_${Date.now()}_${item.htmDisplayNm}`,
      htmDisplayNm: item.htmDisplayNm || '',
      htmRegDate: item.htmRegDate || '',
      htmDocType: item.htmDocType || '',
      status: '1',
    }));

    setFileList(newFileList);
    // handlePDFUploadCheck(); //필요한 파일 다 올렸는지 체크
  }, [OriginFileList]);

  const handleFileUpload = async (files, htmDocType) => {
    const alertMessage = getAlertMessageFunction();

    if (!magamCertifyCheck(isMagam, isCertify)) return;

    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    if (htmDocType !== 'D' && fileArray.length > 1) {
      //통합파일은 하나만 업로드 가능
      alertMessage(t('uploadPdf.errorMessages.default'));
      return;
    }

    // 파일 목록 추가
    const newFiles = fileArray.map(file => ({
      fileId: `${htmDocType}_${Date.now()}_${file.name}`,
      htmDocType,
      htmDisplayNm: file.name,
      htmRegDate: new Date().toLocaleString(),
      status: '0',
      errorMessage: '', // 에러 메시지 필드 추가
    }));

    setFileList(prev => [...prev, ...newFiles]);

    // 파일별 업로드 처리
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      const fileId = newFiles[i].fileId;

      const formData = new FormData();
      formData.append('pdfFile', file);
      formData.append('pdfType', htmDocType == 'D' ? '2' : '1');
      formData.append('tblType', '1');
      // formData.append('mobileFlag', 'Y');
      // formData.append('htmFileNm', file.name);
      console.log('file', file);
      console.log('htmDocType', htmDocType);
      console.log('file', file);

      try {
        // 업로드 시작 시 진행률 0 설정
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: 0,
        }));

        const response = await dispatch(
          uploadPDF({
            data: formData,
            onProgress: progress => {
              setUploadProgress(prev => ({
                ...prev,
                [fileId]: progress,
              }));
            },
          }),
        ).unwrap();

        if (response.resultCode === true) {
          // 업로드 완료 처리
          setFileList(prev =>
            prev.map(item => (item.fileId === fileId ? { ...item, status: '1' } : item)),
          );

          // 프로그레스바 종료
          setUploadProgress(prev => {
            const { [fileId]: removed, ...rest } = prev;
            return rest;
          });
        } else if (response.resultErrCode) {
          // resultErrCode가 있는 경우 에러 메시지 설정
          const errorMessage = getEDocumentErrorMessages(t, response.resultErrCode);

          setFileList(prev =>
            prev.map(item =>
              item.fileId === fileId
                ? { ...item, status: 'error', errorMessage: errorMessage }
                : item,
            ),
          );

          // 002, 014 에러코드일 경우 슬라이드 다이얼로그 표시
          if (response.resultErrCode === '002') {
            setErrorDialogDescription(t('uploadPdf.errorMessages.slideDialog.description'));
            setErrorDialogOpen(true);
          } else if (response.resultErrCode === '014') {
            setErrorDialogDescription(
              t('uploadPdf.errorMessages.slideDialog.description2', {
                year: TARGET_YEAR,
                nextYear: TARGET_YEAR + 1,
              }),
            );
            setErrorDialogOpen(true);
          }

          // 프로그레스바 종료
          setUploadProgress(prev => {
            const { [fileId]: removed, ...rest } = prev;
            return rest;
          });
        } else {
          setFileList(prev =>
            prev.map(item =>
              item.fileId === fileId
                ? { ...item, status: 'error', errorMessage: t('uploadPdf.errorMessages.default') }
                : item,
            ),
          );
        }
      } catch (error) {
        console.error('File upload failed:', error);
        // 에러 처리 - 실패한 파일 표시
        setFileList(prev =>
          prev.map(item =>
            item.fileId === fileId
              ? { ...item, status: 'error', errorMessage: t('uploadPdf.errorMessages.default') }
              : item,
          ),
        );

        // 프로그레스바 종료
        setUploadProgress(prev => {
          const { [fileId]: removed, ...rest } = prev;
          return rest;
        });
      }
    }

    // 모든 업로드가 완료된 후 setReload 디스패치
    // 위변조 등등 errorMessage 보여주기위해 setReload는 onClose될때 처리한다
    // dispatch(setReload());
    handlePDFUploadCheck();
  };

  const handlePDFUploadCheck = async () => {
    try {
      //파일 업로드 다 됐는지 ?
      const alertMessage = getAlertMessageFunction();
      const response = await dispatch(getPDFUploadCheck()).unwrap();
      console.log('response', response);
      if (response.resultCode === '1') {
        // 확정 처리
        const result = await dispatch(pdfConfirm()).unwrap();
        //확정되면 창 닫는다
        alertMessage(t('uploadPdf.success'));
        setNotAllFile(false);
        onClose();
        console.log('result', result);
      } else {
        //상세일때 파일 업로드 안된 경우 표시
        if (uploadFlag === 'D') {
          setNotAllFile(true);
        }
      }
    } catch (error) {
      console.error('PDF upload check failed:', error);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    dispatch(setReload());
  };

  return (
    <SlideRightDialog isOpen={isOpen} onClose={onClose} title={t('uploadPdf.title')}>
      <div className="uploadPdfContainer">
        <InfoBanner
          title={`'${myInfo?.htmInsaPerNm || ''}' ${t('uploadPdf.banner.title')} ${
            //flag1 : 중도 입사, flag2 : 계속 근로자
            uploadFlag === 'D' ? t('uploadPdf.banner.flag1') : t('uploadPdf.banner.flag2')
          } ${t('uploadPdf.banner.title2')}`}
          description={
            uploadFlag === 'D'
              ? t('uploadPdf.banner.description1')
              : t('uploadPdf.banner.description2')
          }
        />

        {/* 통합파일 */}
        <FileUploadSection
          title={t('uploadPdf.uploadFileTitle')}
          fileRef={fileInputRef}
          fileList={fileList}
          htmDocType="A"
          onUpload={files => handleFileUpload(files, 'A')}
          t={t}
          uploadProgress={uploadProgress}
        />
        {/* 상세파일업로드 */}
        {uploadFlag === 'D' && (
          <FileUploadSection
            title={t('uploadPdf.detailFileTitle')}
            fileRef={detailFileInputRef}
            fileList={fileList}
            htmDocType="D"
            multiple
            onUpload={files => handleFileUpload(files, 'D')}
            t={t}
            uploadProgress={uploadProgress}
            notAllFile={notAllFile}
          />
        )}

        {/* 에러 슬라이드 다이얼로그 (002, 014 에러코드) */}
        <SlideDialogV
          isOpen={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          title={t('uploadPdf.errorMessages.slideDialog.title')}
          description={errorDialogDescription}
          buttons={[
            {
              label: '확인',
              onClick: () => setErrorDialogOpen(false),
              color: 'primary',
            },
          ]}
        />
      </div>
    </SlideRightDialog>
  );
};

export default UploadPdf;
